/**
 * GovCostAnalysisExportModal — client-side PDF summary + XLSX bid schedule.
 * Uses browser-native print for PDF (opens print dialog with clean summary view).
 * XLSX built with SheetJS/xlsx on the fly.
 */
import React, { useState } from 'react';
import api from '../../services/api';
import { S, GOV_COLORS, fmtMoneyFull } from './govShared';

interface Props {
  versionId: string;
  onClose: () => void;
}

export default function GovCostAnalysisExportModal({ versionId, onClose }: Props) {
  const [busy, setBusy] = useState<string | null>(null);

  const fetchPayload = async () => {
    const { data } = await api.get(`/gov/cost-analysis-versions/${versionId}/export/summary.json`);
    return data;
  };

  const exportPdf = async () => {
    setBusy('pdf');
    try {
      const p = await fetchPayload();
      const html = renderPrintableHtml(p);
      const win = window.open('', '_blank');
      if (!win) { alert('Popup blocked — allow popups for this site'); return; }
      win.document.write(html);
      win.document.close();
      // Give it a moment to layout, then trigger print
      setTimeout(() => { try { win.print(); } catch {} }, 500);
      onClose();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Export failed');
    } finally { setBusy(null); }
  };

  const exportXlsx = async () => {
    setBusy('xlsx');
    try {
      const p = await fetchPayload();
      const { version, items } = p;
      // Build CSV-shaped data — browser-safe, no xlsx dep needed
      const rows = [
        ['Line', 'Category', 'CSI', 'Description', 'Cost Type', 'Qty', 'Unit',
         'Low Unit $', 'Expected Unit $', 'High Unit $',
         'Low Ext $', 'Expected Ext $', 'High Ext $',
         'Included', 'Alternate', 'Pricing Method', 'Assumption'],
        ...items.map((i: any) => [
          i.sort_order, i.category || '', i.csi_code || '', i.description, i.cost_type,
          i.quantity, i.unit,
          Number(i.low_unit_cost || 0), Number(i.expected_unit_cost || 0), Number(i.high_unit_cost || 0),
          Number(i.low_extended_cost || 0), Number(i.expected_extended_cost || 0), Number(i.high_extended_cost || 0),
          i.is_included ? 'Y' : 'N', i.is_alternate ? 'Y' : 'N',
          i.pricing_method || '', i.assumption || '',
        ]),
        [],
        ['SUMMARY'],
        ['Direct Low', '', '', '', '', '', '', '', '', '', Number(version.direct_low || 0)],
        ['Direct Expected', '', '', '', '', '', '', '', '', '', '', Number(version.direct_expected || 0)],
        ['Direct High', '', '', '', '', '', '', '', '', '', '', '', Number(version.direct_high || 0)],
        ['Contingency %', version.contingency_pct],
        ['O&P %', version.op_pct],
        ['Bond %', version.bond_pct],
        ['Risk Buffer $', version.risk_buffer_fixed],
        ['Strategic Adj $', version.strategic_adjustment_fixed],
        ['Total Low', '', '', '', '', '', '', '', '', '', Number(version.total_low || 0)],
        ['Total Expected', '', '', '', '', '', '', '', '', '', '', Number(version.total_expected || 0)],
        ['Total High', '', '', '', '', '', '', '', '', '', '', '', Number(version.total_high || 0)],
        ['Recommended Bid', '', '', '', '', '', '', '', '', '', '', '', Number(version.recommended_bid || 0)],
      ];
      const csv = rows.map((r: any[]) => r.map((cell: any) => {
        const s = String(cell ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const solNum = p.version?.analysis?.solicitation?.solicitation_number || 'estimate';
      a.download = `${solNum}-cost-estimate-v${version.version_number}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onClose();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Export failed');
    } finally { setBusy(null); }
  };

  const exportJson = async () => {
    setBusy('json');
    try {
      const p = await fetchPayload();
      const blob = new Blob([JSON.stringify(p, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const solNum = p.version?.analysis?.solicitation?.solicitation_number || 'estimate';
      a.download = `${solNum}-cost-estimate-v${p.version?.version_number}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onClose();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Export failed');
    } finally { setBusy(null); }
  };

  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={{ ...S.modal, width: 480 }} onClick={e => e.stopPropagation()}>
        <h2 style={S.modalTitle}>📤 Export Cost Analysis</h2>
        <p style={{ color: GOV_COLORS.textMuted, fontSize: 13, marginBottom: 20 }}>
          Choose an export format for this cost estimate.
        </p>

        <button onClick={exportPdf} disabled={!!busy} style={{ ...S.addBtn, width: '100%', padding: '14px', marginBottom: 8, opacity: busy === 'pdf' ? 0.6 : 1 }}>
          {busy === 'pdf' ? 'Generating…' : '📄 Print / Save as PDF (Full Summary)'}
        </button>
        <p style={{ color: GOV_COLORS.textMuted, fontSize: 11, marginBottom: 16 }}>Opens print dialog with formatted summary. Save as PDF from there.</p>

        <button onClick={exportXlsx} disabled={!!busy} style={{ ...S.addBtn, width: '100%', padding: '14px', marginBottom: 8, opacity: busy === 'xlsx' ? 0.6 : 1 }}>
          {busy === 'xlsx' ? 'Generating…' : '📊 CSV Bid Schedule (opens in Excel)'}
        </button>
        <p style={{ color: GOV_COLORS.textMuted, fontSize: 11, marginBottom: 16 }}>Line items + totals in spreadsheet form.</p>

        <button onClick={exportJson} disabled={!!busy} style={{ ...S.cancelBtn, width: '100%', padding: '14px', marginBottom: 16, opacity: busy === 'json' ? 0.6 : 1 }}>
          {busy === 'json' ? 'Generating…' : '💾 JSON (raw data, all fields)'}
        </button>

        <div style={S.modalFooter}>
          <button onClick={onClose} style={{ ...S.cancelBtn, flex: 1 }}>Close</button>
        </div>
      </div>
    </div>
  );
}

function renderPrintableHtml(p: any): string {
  const v = p.version;
  const sol = v.analysis?.solicitation || {};
  const money = (n: any) => fmtMoneyFull(Number(n || 0));
  const grouped: Record<string, any[]> = {};
  for (const i of p.items || []) {
    const k = i.category || 'Uncategorized';
    (grouped[k] ||= []).push(i);
  }

  const itemRows = Object.entries(grouped).map(([cat, items]: any) => {
    const catTotal = items.reduce((s: number, i: any) => s + (i.is_included !== false ? Number(i.expected_extended_cost || 0) : 0), 0);
    return `
      <tr class="cat-row"><td colspan="5">${escapeHtml(cat)}</td><td class="right">${money(catTotal)}</td></tr>
      ${items.map((i: any) => `
        <tr class="${i.is_included === false ? 'excluded' : ''}${i.is_alternate ? ' alt' : ''}">
          <td>${escapeHtml(i.description)}${i.is_alternate ? ' <em>(alt)</em>' : ''}</td>
          <td class="right">${i.quantity}</td>
          <td>${escapeHtml(i.unit)}</td>
          <td class="right">${money(i.low_unit_cost)}</td>
          <td class="right">${money(i.expected_unit_cost)}</td>
          <td class="right"><strong>${money(i.expected_extended_cost)}</strong></td>
        </tr>
      `).join('')}
    `;
  }).join('');

  return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8" />
<title>${escapeHtml(sol.solicitation_number || 'Cost Estimate')} — Cost Analysis v${v.version_number}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, system-ui, Segoe UI, sans-serif; padding: 32px; color: #111; }
  h1 { margin: 0; font-size: 22px; }
  h2 { font-size: 16px; margin-top: 24px; border-bottom: 2px solid #333; padding-bottom: 4px; }
  .meta { color: #666; font-size: 12px; margin-top: 4px; }
  .kpi-row { display: flex; gap: 12px; margin-top: 16px; }
  .kpi { flex: 1; padding: 12px; border: 1px solid #ccc; border-radius: 6px; }
  .kpi-label { font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: 1px; font-weight: 700; }
  .kpi-value { font-size: 22px; font-weight: 800; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 12px; }
  th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
  th { background: #f0f0f0; font-size: 11px; text-transform: uppercase; }
  .right { text-align: right; }
  .cat-row td { background: #f8f8f8; font-weight: 700; }
  .excluded { color: #999; text-decoration: line-through; }
  .alt { background: #fafafa; }
  .risk, .assumption { padding: 8px; border-left: 4px solid #ccc; margin-bottom: 6px; font-size: 12px; }
  .risk.critical { border-color: #dc2626; }
  .risk.high { border-color: #f97316; }
  .risk.medium { border-color: #eab308; }
  .risk.low { border-color: #22c55e; }
  .assumption.high { border-color: #dc2626; }
  .assumption.medium { border-color: #3b82f6; }
  .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #ccc; color: #666; font-size: 11px; }
  @media print { body { padding: 16px; } h2 { page-break-before: avoid; } table { page-break-inside: auto; } tr { page-break-inside: avoid; } }
</style>
</head>
<body>
  <h1>Cost Analysis — ${escapeHtml(sol.solicitation_number || '(No sol #)')}</h1>
  <div class="meta">
    ${escapeHtml(sol.title || '')}<br />
    ${escapeHtml([sol.agency_name, sol.sub_agency_name, sol.office_name].filter(Boolean).join(' → '))}<br />
    ${escapeHtml(sol.place_of_performance_display || '')} · Deadline ${sol.response_deadline ? new Date(sol.response_deadline).toLocaleDateString() : 'N/A'}<br />
    Version ${v.version_number}${v.name ? ` — ${escapeHtml(v.name)}` : ''} · Status: ${escapeHtml(v.status)} · Calc engine v${v.calculation_version}
  </div>

  ${v.executive_summary ? `<h2>Executive Summary</h2><p>${escapeHtml(v.executive_summary).replace(/\n/g, '<br/>')}</p>` : ''}

  <h2>Cost Summary</h2>
  <div class="kpi-row">
    <div class="kpi"><div class="kpi-label">Low</div><div class="kpi-value">${money(v.total_low)}</div></div>
    <div class="kpi"><div class="kpi-label">Expected</div><div class="kpi-value">${money(v.total_expected)}</div></div>
    <div class="kpi"><div class="kpi-label">High</div><div class="kpi-value">${money(v.total_high)}</div></div>
    <div class="kpi"><div class="kpi-label">Recommended Bid</div><div class="kpi-value">${money(v.recommended_bid)}</div></div>
  </div>
  <table>
    <tr><td>Direct Cost (Expected)</td><td class="right">${money(v.direct_expected)}</td></tr>
    <tr><td>Contingency (${v.contingency_pct}%)</td><td class="right">${money(Number(v.direct_expected) * Number(v.contingency_pct) / 100)}</td></tr>
    <tr><td>Overhead & Profit (${v.op_pct}%)</td><td class="right">applied to direct + contingency</td></tr>
    <tr><td>Bond (${v.bond_pct}%)</td><td class="right">applied after O&P</td></tr>
    <tr><td>Risk Buffer</td><td class="right">${money(v.risk_buffer_fixed)}</td></tr>
    <tr><td>Strategic Adjustment</td><td class="right">${money(v.strategic_adjustment_fixed)}</td></tr>
    <tr><td><strong>Grand Total (Expected)</strong></td><td class="right"><strong>${money(v.total_expected)}</strong></td></tr>
    <tr><td><strong>Recommended Bid (rounded to nearest ${money(v.rounding_increment)})</strong></td><td class="right"><strong>${money(v.recommended_bid)}</strong></td></tr>
  </table>

  <h2>Line Items</h2>
  <table>
    <thead><tr><th>Description</th><th class="right">Qty</th><th>Unit</th><th class="right">Low $</th><th class="right">Expected $</th><th class="right">Ext $</th></tr></thead>
    <tbody>${itemRows}</tbody>
  </table>

  ${(p.risks && p.risks.length) ? `<h2>Risk Register</h2>${p.risks.map((r: any) => `
    <div class="risk ${escapeHtml(r.severity)}">
      <strong>[${escapeHtml(r.severity)}] ${escapeHtml(r.title)}</strong><br />
      ${escapeHtml(r.description).replace(/\n/g, '<br/>')}
      ${r.mitigation ? `<br /><em>Mitigation: ${escapeHtml(r.mitigation)}</em>` : ''}
      ${r.cost_exposure_low || r.cost_exposure_high ? `<br />Exposure: ${money(r.cost_exposure_low)} – ${money(r.cost_exposure_high)}` : ''}
    </div>`).join('')}` : ''}

  ${(p.assumptions && p.assumptions.length) ? `<h2>Assumptions</h2>${p.assumptions.map((a: any) => `
    <div class="assumption ${escapeHtml(a.importance)}">
      ${a.category ? `<strong>[${escapeHtml(a.category)}]</strong> ` : ''}${escapeHtml(a.assumption)}
      ${a.requires_confirmation && !a.confirmed ? ' <em>(requires confirmation)</em>' : ''}
    </div>`).join('')}` : ''}

  <div class="footer">
    Generated ${new Date().toLocaleString()} · McMullen Browne Development LLC<br />
    ${v.finalized_at ? `Finalized ${new Date(v.finalized_at).toLocaleString()} by ${escapeHtml(v.approver_name_stamp || '')}` : 'Draft — not yet finalized'}<br />
    Cost engine version ${v.calculation_version}${v.ai_model ? ` · AI: ${escapeHtml(v.ai_model)}` : ''}
  </div>
</body></html>`;
}

function escapeHtml(s: any): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}
